import {Component} from '@angular/core';
import {NbDialogService} from '@nebular/theme';
import {SubscribedComponent} from '../subscribed.component';
import {TimeApprovalRequest, TimeTrackingService, TrackedStatus} from '../shared/time-tracking.service';
import {ReasonDialogComponent} from '../dialogs/reason-dialog/reason-dialog.component';
import {Globals} from '../shared/Globals';
import {ApprovalTrip} from '../interfaces/trip';
import {AuthService} from '../shared/auth.service';
import moment from 'moment';

/**
 * Full-screen week detail, opened from the approvals overview via PopupDialogComponent.
 * Carries the entries table, per-project + billable subtotals, event log, and the
 * Approve/Reject actions — the drill-down that used to be an inline expanded row.
 * `request`, `currentUserId` and `onDecision` are injected by the host via
 * componentContext; on a successful decision it calls onDecision() so the host
 * closes the dialog and refreshes the queue.
 */
@Component({
  selector: 'bst-time-approval-detail',
  templateUrl: './time-approval-detail.component.html',
  styleUrls: ['./time-approval-detail.component.scss'],
  standalone: false
})
export class TimeApprovalDetailComponent extends SubscribedComponent {
  TrackedStatus = TrackedStatus;
  Globals = Globals;

  // Injected via PopupDialogComponent's componentContext.
  request: TimeApprovalRequest = null;
  currentUserId: number = null;
  onDecision: () => void = null;
  trips: ApprovalTrip[] = [];
  days: Date[] = [];
  weekNumber: number = null;
  dayTotals: number[] = Array(7).fill(0);
  groups: {id: number, project: string, color: string, client: string, days: number[], total: number,
    rows: {Phase_id: number, phase: string, description: string, days: number[], total: number}[]}[] = [];

  constructor(private time: TimeTrackingService, private dialogService: NbDialogService, private auth: AuthService) {
    super();
  }

  // The week's trips (#874) come from their own endpoint: the list payload carries no addresses.
  async ngOnInit() {
    super.ngOnInit();
    this.buildGrid();
    if (this.request?.id && this.auth.user.getValue()?.trips_enabled) {
      this.trips = await this.time.getApprovalTrips(this.request.id).catch(() => []);
    }
  }

  address(t: ApprovalTrip, end: 'from' | 'to'): string {
    return `${t[end + '_street']} ${t[end + '_number']}, ${t[end + '_postcode']} ${t[end + '_town']}`;
  }

  get tripKm(): number {
    return this.trips.reduce((sum, t) => sum + (+t.km || 0), 0);
  }

  get canDecide(): boolean {
    return !!this.request
      && this.request.status === TrackedStatus.Submitted
      && this.request.User_id !== this.currentUserId;
  }

  employeeName(r: TimeApprovalRequest): string {
    return [r?.employee?.name_first, r?.employee?.name_middle, r?.employee?.name_last].filter(Boolean).join(' ');
  }

  statusLabel(status: TrackedStatus): string {
    switch (status) {
      case TrackedStatus.Submitted: return 'Pending';
      case TrackedStatus.Approved: return 'Approved';
      case TrackedStatus.Rejected: return 'Rejected';
      default: return 'Draft';
    }
  }

  // Same week colours as the timesheet and Km: the week's status colours every filled cell.
  get cellState(): string {
    switch (this.request?.status) {
      case TrackedStatus.Submitted: return 'st-sub';
      case TrackedStatus.Approved: return 'st-appr';
      case TrackedStatus.Rejected: return 'st-rej';
      default: return 'st-draft';
    }
  }

  // Hours' calendar model (timesheet buildWeeks): one card per project, one row per phase + note,
  // seconds per weekday (Mon = 0). Built once: the dialog shows one fixed request.
  private buildGrid() {
    const start = moment(this.request?.week_start).startOf('day');
    this.days = Array.from({length: 7}, (_, i) => start.clone().add(i, 'days').toDate());
    this.weekNumber = start.isoWeek();
    this.groups = [];
    for (const e of (this.request?.entries || [])) {
      const phase = e.user_to_phase?.phase, sec = e.tracked_duration || 0;
      const day = moment(e.tracked_on).startOf('day').diff(start, 'days');
      let g = this.groups.find(x => x.id === phase?.Project_id);
      if (!g) {
        g = {id: phase?.Project_id, project: phase?.project?.name || 'No project', color: phase?.project?.color,
          client: phase?.project?.for_client?.name, days: Array(7).fill(0), total: 0, rows: []};
        this.groups.push(g);
      }
      const description = (e.description || '').trim();
      let row = g.rows.find(x => x.Phase_id === phase?.id && x.description.toUpperCase() === description.toUpperCase());
      if (!row) {
        row = {Phase_id: phase?.id, phase: phase?.name, description, days: Array(7).fill(0), total: 0};
        g.rows.push(row);
      }
      if (day >= 0 && day < 7) { row.days[day] += sec; g.days[day] += sec; this.dayTotals[day] += sec; }
      row.total += sec;
      g.total += sec;
    }
  }

  totalSeconds(r: TimeApprovalRequest): number {
    return (r?.entries || []).reduce((sum, e) => sum + (e.tracked_duration || 0), 0);
  }

  billableSeconds(r: TimeApprovalRequest): number {
    return (r?.entries || [])
      .filter(e => e.user_to_phase?.phase?.project?.is_billable)
      .reduce((sum, e) => sum + (e.tracked_duration || 0), 0);
  }

  reviewerNames(r: TimeApprovalRequest): string {
    const seen = new Set<number>();
    return (r?.assignees || [])
      .filter(a => a.status === 1 && a.User_id != null && !seen.has(a.User_id) && seen.add(a.User_id))
      .map(a => [a.user?.name_first, a.user?.name_last].filter(Boolean).join(' '))
      .filter(Boolean)
      .join(', ');
  }

  duration(seconds: number): string {
    const hours = Math.floor((seconds || 0) / 3600);
    const minutes = Math.round(((seconds || 0) % 3600) / 60);
    return `${hours}h ${minutes.toString().padStart(2, '0')}m`;
  }

  // Friendly past-tense label for the raw backend event_type ('submit' → 'Submitted'),
  // so the log reads like the rest of the app's titlecased states. Unknown types fall
  // back to a titlecased, de-underscored form.
  eventLabel(type: string): string {
    const map: {[k: string]: string} = {
      submit: 'Submitted',
      resubmit: 'Resubmitted',
      approve: 'Approved',
      reject: 'Rejected',
      reopen: 'Reopened',
      recall: 'Withdrawn',
      draft_edit: 'Edited',
      auto_approve: 'Auto-approved',
    };
    if (!type) { return ''; }
    return map[type] ?? type.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());
  }

  async approve() {
    if (this.isLoading || !this.request) { return; }
    this.loading.next(true);
    try {
      await this.time.approveRequest(this.request.id);
      this.onDecision?.();
    } catch {
      // The service already toasts the server message; a conflict (week already
      // decided/recalled) is surfaced there. Closing + refreshing clears the dead row.
      this.onDecision?.();
    } finally {
      this.loading.next(false);
    }
  }

  async reject() {
    if (this.isLoading || !this.request) { return; }
    const reason = await this.askReason();
    if (!reason) { return; }
    this.loading.next(true);
    try {
      await this.time.rejectRequest(this.request.id, reason);
      this.onDecision?.();
    } catch {
      this.onDecision?.();
    } finally {
      this.loading.next(false);
    }
  }

  private askReason(): Promise<string | null> {
    return new Promise(resolve => {
      this.dialogService.open(ReasonDialogComponent, {
        context: {
          title: 'Reject week',
          subtitle: 'The employee will see this reason on their timesheet.',
          placeholder: 'Reason for rejection',
          confirmLabel: 'Reject',
          status: 'danger',
        }
      }).onClose.subscribe((reason: string | null) => resolve(reason ?? null));
    });
  }
}
